import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MenuPermission } from '@/types';
import { menuPermissionsApi } from '@/api/services/menuPermissions';
import { useAuth } from './useAuth';

interface PermissionContextType {
  permissions: Record<string, boolean>;
  isLoading: boolean;
  hasPermission: (menuId: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  const loadPermissions = async () => {
    if (!user) {
      setPermissions({});
      setIsLoading(false);
      return;
    }

    // Admin hat alles
    if (user.is_superuser) {
      setPermissions({});  // Leeres Objekt = alles erlaubt
      setIsLoading(false);
      return;
    }

    // Bestimme Rolle mit Priorität: director > manager > employee
    const roles = user.assigned_branches?.map((b: any) => b.role) || [];
    let userRole = 'employee';
    if (roles.includes('director')) {
      userRole = 'director';
    } else if (roles.includes('manager')) {
      userRole = 'manager';
    } else if (roles.includes('employee')) {
      userRole = 'employee';
    }
    console.log('🔍 PermissionContext: User role:', userRole, 'All roles:', roles);
    
    try {
      const response = await menuPermissionsApi.getForRole(userRole);
      console.log('📡 PermissionContext: API Response:', response.data);
      
      const permMap: Record<string, boolean> = {};
      
      // Prüfe ob paginiert
      const permissionsArray = Array.isArray(response.data) 
        ? response.data 
        : ((response.data as any)?.results || []);
      
      console.log('📊 PermissionContext: Permissions Array:', permissionsArray.length, 'items');
      
      permissionsArray.forEach((perm: any) => {
        permMap[perm.menu_id] = perm.is_allowed;
      });
      
      console.log('✅ PermissionContext: Permission Map:', permMap);
      setPermissions(permMap);
    } catch (error) {
      console.error('❌ PermissionContext: Failed to load permissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [user]);

  const hasPermission = (menuId: string): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return permissions[menuId] === true;
  };

  return (
    <PermissionContext.Provider 
      value={{ permissions, isLoading, hasPermission, refreshPermissions: loadPermissions }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) throw new Error('usePermissions must be used within PermissionProvider');
  return context;
};

